import { useCallback, useMemo } from 'react';

import { useUser } from '@auth-v2';
import { useAtomValue } from 'jotai';
import { useNavigate } from 'react-router';

import { useStudentsForCoach, useSwingThreadsByStudentIds, useThreadMessageUnreadNotifications } from '@queries';
import { activeUserIdAtom } from '@store';
import { Box, BubbleTextIcon, colors, Heading4, Select, Stack, u } from '@ui';

const STUDENT_FILTER_THRESHOLD = 30;

type GolferSelectionProps = {
    studentId: string | null;
    getOverviewLink: (golfer: string) => string;
};

export const GolferSelection = ({ studentId, getOverviewLink }: GolferSelectionProps) => {
    const activeUserId = useAtomValue(activeUserIdAtom);
    const { data: user } = useUser();
    const navigate = useNavigate();
    const { data: students } = useStudentsForCoach(user?.id || '');
    const { data: unreadNotifications } = useThreadMessageUnreadNotifications();

    const golferIds = useMemo(() => {
        const ids = students?.map((s) => s.userId) ?? [];
        if (activeUserId) {
            ids.push(activeUserId);
        }
        return ids;
    }, [students, activeUserId]);

    const { data: hasThread } = useSwingThreadsByStudentIds(golferIds);

    const golfers = useMemo(
        () =>
            [
                ...(user ? [{ userId: user.id, name: user?.user_metadata?.['name'] || '' }] : []),
                ...(students?.map((student) => ({ userId: student.userId, name: student.name })) ?? []),
            ].map((golfer) => {
                let label: string | React.ReactNode = golfer.name;
                const hasUnread = unreadNotifications?.sourceUsers?.[golfer.userId];
                if (hasThread?.[golfer.userId]) {
                    label = (
                        <Box display="flex" gap={u(1)}>
                            {golfer.name} <BubbleTextIcon color={hasUnread ? colors.teal[500] : colors.bluegray[500]} />
                        </Box>
                    );
                }
                return { value: golfer.userId, label, searchLabel: golfer.name };
            }),
        [user, students, unreadNotifications?.sourceUsers, hasThread],
    );

    const changeUser = useCallback(
        ([golferId]: string[]) => {
            // need to figure out how to switch users
            navigate(getOverviewLink(golferId), { replace: true });
        },
        [navigate, getOverviewLink],
    );

    return (
        golfers &&
        golfers.length > 1 && (
            <Stack width="100%">
                <Heading4>Golfer</Heading4>
                <Select
                    options={golfers}
                    selectedValue={studentId || golfers[0]?.value}
                    onChange={changeUser}
                    label="Golfer"
                    placeholder="Golfer"
                    searchBox={(students?.length ?? 0) > STUDENT_FILTER_THRESHOLD}
                    autoSelect={false}
                    width="100%"
                />
            </Stack>
        )
    );
};
